import { useEffect, useRef, useState } from 'react';
import Image from 'next/image';
import { getCDNUrl } from '@util/index';
import SafeImage from '@ui/SafeImage';
import ProductViewerModal from '@c/modals/ProductViewerModal';
import { ProductDocument } from '@models/product';
import HeartIconButton from '@c/icons/buttons/HeartButton';

type ProductImagesProps = {
  product: ProductDocument;
};

type ProductImagesCarouselProps = {
  images: {
    full: string;
    thumb: string;
  }[];
  openProductViewer: (index: number) => void;
};

const ProductImages = ({ product }: ProductImagesProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  if (!product?.images) {
    return (
      <Image
        alt="No Images Found"
        width={500}
        height={500}
        src="/not_found.svg"
      />
    );
  }

  const openProductViewer = (index: number) => {
    setCurrentImageIndex(index);
    setIsModalOpen(true);
  };

  return (
    <div className="relative w-full overflow-x-auto">
      <ProductImagesCarousel
        images={product.images}
        openProductViewer={openProductViewer}
      />
      <div className="absolute right-[1.6rem] top-[2.4rem] z-30  cursor-pointer md:right-[5rem] lg:right-[10rem]">
        <HeartIconButton
          itemId={product.id}
          initialCount={product.favorite_count ?? 0}
        />
      </div>
      {product.is_featured && (
        <div className="absolute left-[1.6rem] top-[2.4rem] z-30 w-fit  cursor-pointer md:left-[5rem] lg:left-[10rem]">
          <div className="flex items-center gap-[0.4rem] rounded-[0.6rem] bg-brand-secondary px-[1.6rem] py-[0.5rem] text-[1.4rem] text-brand-white">
            <span>
              <svg
                width="16"
                height="17"
                viewBox="0 0 16 17"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M2.66699 16.5V0.5H13.3337V16.5L8.00033 13.8333L2.66699 16.5Z"
                  fill="white"
                />
                <path
                  d="M8.77838 6.52631L8.00078 3.96631L7.22318 6.52631H4.80078L6.77838 7.93751L6.02638 10.3663L8.00078 8.86551L9.97838 10.3663L9.22638 7.93751L11.2008 6.52631H8.77838Z"
                  fill="#C41719"
                />
              </svg>
            </span>
            <span>Featured</span>
          </div>
        </div>
      )}

      <div className="absolute bottom-[2.4rem] right-[1.6rem] z-30  cursor-pointer md:right-[5rem] lg:right-[10rem]">
        <div
          className="flex items-center gap-[0.4rem] rounded-[0.6rem] bg-brand-black px-[1.6rem] py-[0.5rem] text-[1.4rem] text-brand-white"
          onClick={() => setIsModalOpen(true)}
        >
          <span>
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13.833 2.66683V2.16683H13.333H5.33301H4.83301V2.66683V10.6668V11.1668H5.33301H13.333H13.833V10.6668V2.66683ZM9.26265 9.50016L10.446 8.02099L11.6272 9.50016H9.26265ZM7.66555 8.61402L8.3282 9.50016H7.00028L7.66555 8.61402ZM2.16634 13.3335V13.8335H2.66634H11.4997V14.1668H2.66634C2.20915 14.1668 1.83301 13.7907 1.83301 13.3335V4.50016H2.16634V13.3335ZM5.33301 1.8335H13.333C13.7902 1.8335 14.1663 2.20964 14.1663 2.66683V10.6668C14.1663 11.124 13.7902 11.5002 13.333 11.5002H5.33301C4.87582 11.5002 4.49967 11.124 4.49967 10.6668V2.66683C4.49967 2.20964 4.87582 1.8335 5.33301 1.8335Z"
                fill="black"
                stroke="white"
              />
            </svg>
          </span>
          <span>See all photos</span>
        </div>
      </div>
      {isModalOpen && (
        <ProductViewerModal
          key={currentImageIndex}
          isOpen={isModalOpen}
          dismiss={() => setIsModalOpen(false)}
          images={product.images}
          initialImage={currentImageIndex}
        />
      )}
    </div>
  );
};

const ProductImagesCarousel = ({
  images,
  openProductViewer,
}: ProductImagesCarouselProps) => {
  const divRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (divRef.current!.offsetWidth > 820) {
      divRef.current?.scrollTo(500, 0);
    }
  }, []);

  return (
    <div
      className="no-scrollbar flex w-full overflow-auto scroll-smooth"
      ref={divRef}
    >
      {images.map((pic, index) => (
        <div
          key={pic.full}
          onClick={() => openProductViewer(index)}
          className="md:min-w-4/5 max-h-[40rem] min-h-[40rem] min-w-full lg:min-w-[80rem]"
        >
          <SafeImage
            src={getCDNUrl(pic.full)}
            alt={'Product Image'}
            skipOptimize
            classes=" object-center object-cover w-full h-full"
          />
        </div>
      ))}
    </div>
  );
};

export default ProductImages;
